import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function EmailLogin() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [stage, setStage] = useState("email"); // email | setpw | activate | login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState("");
  const [isExempt, setIsExempt] = useState(false);
  const [busy, setBusy] = useState(false);
  const err = (e) => toast.error(e?.response?.data?.detail || "Er ging iets mis");

  const checkStatus = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/backend-auth/status?email=${encodeURIComponent(email)}`);
      setIsExempt(!!r.data.no_2fa || !r.data.totp_enabled);
      setStage(r.data.must_set_password ? "setpw" : "login");
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const doSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/backend-auth/setup", { email, password });
      if (r.data.no_2fa) {
        setIsExempt(true); setStage("login"); setCode("");
        toast.success("Wachtwoord ingesteld — log nu in");
      } else {
        setQr(r.data.qr); setStage("activate"); setCode("");
        toast.success("Wachtwoord ingesteld — scan de QR-code");
      }
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const doActivate = async () => {
    setBusy(true);
    try {
      await api.post("/backend-auth/activate", { email, code });
      toast.success("2FA geactiveerd — log nu in"); setStage("login"); setCode("");
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const doLogin = async () => {
    setBusy(true);
    try {
      await api.post("/backend-auth/login", { email, password, code });
      await checkAuth();
      navigate("/app");
    } catch (e) {
      if (e?.response?.data?.detail === "2FA_KOPPELEN") {
        try {
          const r = await api.post("/backend-auth/setup-2fa", { email, password });
          setQr(r.data.qr); setCode(""); setStage("activate");
          toast.info("Koppel eerst uw authenticator-app");
        } catch (e2) { err(e2); }
      } else err(e);
    } finally { setBusy(false); }
  };
  const doForgot = async () => {
    setBusy(true);
    try {
      await api.post("/backend-auth/forgot-password", { email });
      toast.success("Als dit account bestaat, is er een herstellink verstuurd.");
      setStage("login");
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5EF] p-6">
      <div className="w-full max-w-md bg-white border border-[#C9A227]/30 border-t-4 border-t-[#0F1E3D] rounded-2xl shadow-sm p-8 space-y-5 animate-fade-in-up">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-500"><ArrowLeft className="h-4 w-4" /> Terug</button>
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0F1E3D] flex items-center justify-center"><ShieldCheck className="h-6 w-6 text-[#C9A227]" /></div>
          <div><h1 className="font-heading text-xl font-bold text-[#0F1E3D]">Praktisch-Debiteurenbeheer — Beheerlogin</h1><p className="text-xs text-slate-500">E-mail + wachtwoord{isExempt ? "" : " + 2FA"}</p></div>
        </div>

        {stage === "email" && (
          <div className="space-y-3">
            <div><Label>E-mailadres</Label><Input type="email" data-testid="bo-email-input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={checkStatus} disabled={busy || !email} data-testid="bo-continue-button">Doorgaan</Button>
          </div>
        )}
        {stage === "setpw" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Eerste keer inloggen: stel een wachtwoord in (min. 8 tekens).</p>
            <div><Label>Nieuw wachtwoord</Label><Input type="password" data-testid="bo-newpw-input" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
            <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={doSetup} disabled={busy || password.length < 8} data-testid="bo-setpw-button">Wachtwoord instellen</Button>
          </div>
        )}
        {stage === "activate" && (
          <div className="space-y-3 text-center">
            <p className="text-sm text-slate-600">Scan met Google Authenticator en voer de 6-cijferige code in.</p>
            {qr && <img src={qr} alt="2FA QR" className="mx-auto h-44 w-44 border rounded-lg" data-testid="bo-qr" />}
            <Input className="text-center tracking-widest" placeholder="000000" data-testid="bo-activate-code" value={code} onChange={(e) => setCode(e.target.value)} />
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={doActivate} disabled={busy || code.length < 6} data-testid="bo-activate-button">2FA activeren</Button>
          </div>
        )}
        {stage === "login" && (
          <div className="space-y-3">
            <div><Label>E-mailadres</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div><Label>Wachtwoord</Label><Input type="password" data-testid="bo-login-pw" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
            {!isExempt && (
              <div><Label>2FA-code</Label><Input className="tracking-widest" placeholder="000000" data-testid="bo-login-code" value={code} onChange={(e) => setCode(e.target.value)} /></div>
            )}
            <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={doLogin} disabled={busy || !password || (!isExempt && code.length < 6)} data-testid="bo-login-button">Inloggen</Button>
            <button type="button" onClick={() => setStage("forgot")} className="w-full text-center text-xs text-[#0F1E3D] hover:text-[#C9A227] hover:underline" data-testid="bo-forgot-link">Wachtwoord vergeten?</button>
          </div>
        )}
        {stage === "forgot" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Vul uw e-mailadres in; we sturen u een link om een nieuw wachtwoord in te stellen.</p>
            <div><Label>E-mailadres</Label><Input type="email" data-testid="bo-forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={doForgot} disabled={busy || !email} data-testid="bo-forgot-submit">Herstellink versturen</Button>
            <button type="button" onClick={() => setStage("login")} className="w-full text-center text-xs text-slate-500 hover:underline">Terug naar inloggen</button>
          </div>
        )}
        <p className="text-xs text-slate-400 text-center">Sessie verloopt na 4 uur; daarna opnieuw inloggen.</p>
      </div>
    </div>
  );
}
